<template>
  <v-container class="py-8">
    <!-- Back navigation -->
    <v-row>
      <v-col cols="12">
        <v-btn
          variant="outlined"
          color="primary"
          size="small"
          @click="$router.push({ name: 'prompt-management-templates' })"
          class="mb-6"
        >
          <template #prepend>
            <v-icon size="18">mdi-chevron-left</v-icon>
          </template>
          {{ $t('promptmanagementtemplates.backToTemplateList') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- ═══════════════════════════════════════════════
         VIEW MODE — Rich read-only detail layout
         ═══════════════════════════════════════════════ -->
    <template v-if="isView">
      <!-- Header: icon + name + domain chip + Edit button -->
      <v-row class="px-2" align="start">
        <v-col>
          <div class="d-flex align-center ga-3 flex-wrap">
            <div
              class="d-flex align-center justify-center flex-shrink-0"
              style="background-color: #BBDEFB; border-radius: 10px; width: 56px; height: 56px;"
            >
              <v-icon size="30" color="#0D3B6E">mdi-file-document-outline</v-icon>
            </div>
            <div>
              <h1 class="text-h4 font-weight-bold" style="line-height: 1.2;">{{ form.template_name }}</h1>
              <v-chip size="small" color="#0D3B6E" variant="tonal" class="mt-2">
                <v-icon start size="14">mdi-domain</v-icon>
                {{ domainLabel }}
              </v-chip>
            </div>
          </div>
        </v-col>
        <v-col cols="auto" class="d-flex align-start">
          <v-btn
            v-if="canEditTemplate"
            prepend-icon="mdi-pencil-outline"
            style="background-color: #0D3B6E; color: white;"
            @click="$router.push({ name: 'prompt-management-template-edit', params: { templateId }, query: { domain: form.domain_id } })"
          >
            {{ $t('promptmanagementtemplates.editTemplate') }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- Notes card (only if present) -->
      <v-row v-if="form.notes" class="mt-5 px-2">
        <v-col cols="12">
          <v-card variant="outlined" rounded="lg" class="pa-5" style="border-color: #E0E0E0; background-color: #FAFAFA;">
            <div class="d-flex align-center ga-2 mb-2">
              <v-icon size="18" color="#757575">mdi-note-text-outline</v-icon>
              <span style="font-size: 0.78rem; font-weight: 700; color: #9E9E9E; letter-spacing: 0.06em; text-transform: uppercase;">{{ $t('promptmanagementtemplates.notes') }}</span>
            </div>
            <p class="text-body-1" style="color: #212121; white-space: pre-wrap; line-height: 1.75;">{{ form.notes }}</p>
          </v-card>
        </v-col>
      </v-row>

      <!-- Steps section -->
      <v-row class="mt-5 px-2">
        <v-col cols="12">
          <div class="d-flex align-center ga-2 mb-4 pl-1">
            <v-icon color="#0D3B6E" size="22">mdi-format-list-numbered</v-icon>
            <h2 class="text-h6 font-weight-bold" style="color: #000;">{{ $t('promptmanagementtemplates.templateSteps') }}</h2>
            <v-chip size="x-small" color="#0D3B6E" variant="tonal">{{ form.steps.length }}</v-chip>
          </div>

          <div v-if="form.steps && form.steps.length > 0">
            <v-card
              v-for="(step, index) in form.steps"
              :key="index"
              variant="outlined"
              rounded="lg"
              class="mb-4"
              style="border-color: #E0E0E0; overflow: hidden;"
            >
              <!-- Step header -->
              <div class="d-flex align-center ga-3 px-5 py-3" style="background-color: #EEF4FB; border-bottom: 1px solid #E0E0E0;">
                <div
                  class="d-flex align-center justify-center flex-shrink-0"
                  style="width: 28px; height: 28px; background-color: #0D3B6E; border-radius: 50%;"
                >
                  <span style="color: white; font-weight: 700; font-size: 0.85rem;">{{ step.order }}</span>
                </div>
                <span class="text-subtitle-1 font-weight-bold" style="color: #0D3B6E;">{{ step.step_name }}</span>
                <v-spacer />
                <v-chip
                  size="small"
                  variant="tonal"
                  :color="step.show_attachment ? '#0D3B6E' : 'grey'"
                  :prepend-icon="step.show_attachment ? 'mdi-paperclip' : 'mdi-eye-off-outline'"
                >
                  {{ step.show_attachment ? $t('promptmanagementtemplates.attachmentShown') : $t('promptmanagementtemplates.attachmentHidden') }}
                </v-chip>
              </div>
              <!-- Prompt text body -->
              <div class="px-5 py-4" style="background-color: white;">
                <div class="d-flex align-center ga-2 mb-2">
                  <v-icon size="15" color="#BDBDBD">mdi-text</v-icon>
                  <span style="font-size: 0.72rem; font-weight: 700; color: #BDBDBD; letter-spacing: 0.07em; text-transform: uppercase;">Facilitator Prompt</span>
                </div>
                <p class="text-body-2" style="color: #424242; white-space: pre-wrap; line-height: 1.75;">{{ step.facilitator_prompt }}</p>

                <div class="d-flex align-center ga-2 mt-3 mb-1">
                  <v-icon size="15" color="#BDBDBD">mdi-account-voice</v-icon>
                  <span style="font-size: 0.72rem; font-weight: 700; color: #BDBDBD; letter-spacing: 0.07em; text-transform: uppercase;">Persona Prompt</span>
                </div>
                <p class="text-body-2" :style="{ color: step.persona_prompt ? '#424242' : '#9E9E9E', fontStyle: step.persona_prompt ? 'normal' : 'italic', whiteSpace: 'pre-wrap', lineHeight: 1.75 }">
                  {{ step.persona_prompt || 'Using default' }}
                </p>

                <div class="d-flex align-center ga-2 mt-3 mb-1">
                  <v-icon size="15" color="#BDBDBD">mdi-refresh</v-icon>
                  <span style="font-size: 0.72rem; font-weight: 700; color: #BDBDBD; letter-spacing: 0.07em; text-transform: uppercase;">Max Rounds</span>
                </div>
                <p class="text-body-2" style="color: #424242;">
                  {{ step.max_rounds === -1 ? 'Dynamic' : step.max_rounds }}
                </p>

                <template v-if="step.max_rounds === -1">
                  <div class="d-flex align-center ga-2 mt-3 mb-1">
                    <v-icon size="15" color="#BDBDBD">mdi-flag-checkered</v-icon>
                    <span style="font-size: 0.72rem; font-weight: 700; color: #BDBDBD; letter-spacing: 0.07em; text-transform: uppercase;">Facilitator Completion Condition</span>
                  </div>
                  <p class="text-body-2" :style="{ color: step.facilitator_completion_condition ? '#424242' : '#9E9E9E', fontStyle: step.facilitator_completion_condition ? 'normal' : 'italic', whiteSpace: 'pre-wrap', lineHeight: 1.75 }">
                    {{ step.facilitator_completion_condition || 'Using default' }}
                  </p>
                </template>
              </div>
            </v-card>
          </div>
          <div v-else class="text-body-2 text-medium-emphasis text-center py-8">
            {{ $t('promptmanagementtemplates.noStepsView') }}
          </div>
        </v-col>
      </v-row>
    </template>

    <!-- ═══════════════════════════════════════════════
         NEW / EDIT MODE — Editable form layout
         ═══════════════════════════════════════════════ -->
    <template v-else>
      <!-- Page title -->
      <v-row>
        <v-col cols="12" class="pl-8">
          <h1 class="text-h4 font-weight-bold">
            {{ isNew ? $t('promptmanagementtemplates.newMeetingTemplate') : $t('promptmanagementtemplates.editMeetingTemplate') }}
          </h1>
        </v-col>
      </v-row>

      <!-- Basic Information section -->
      <v-row class="mt-8 px-2">
        <v-col cols="12">
          <v-card variant="outlined" rounded="lg" class="pa-6" style="border-color: #9E9E9E; background-color: white;">
            <h2 class="text-h5 font-weight-bold mb-6" style="color: #000;">{{ $t('promptmanagementtemplates.basicInformation') }}</h2>

            <v-row>
              <v-col cols="12" md="6">
                <label class="field-label mb-1 d-block">
                  {{ $t('promptmanagementtemplates.columnTemplateName') }} <span class="text-red font-weight-bold">*</span>
                </label>
                <v-text-field
                  v-model="form.template_name"
                  variant="outlined"
                  color="primary"
                  density="comfortable"
                  maxlength="100"
                  :placeholder="$t('promptmanagementtemplates.enterTemplateName')"
                  :class="fieldErrors.template_name ? 'field-error' : ''"
                  :error="!!fieldErrors.template_name"
                  hide-details
                >
                  <template #append-inner>
                    <span class="char-counter">{{ form.template_name.length }} / 100</span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <label class="field-label mb-1 d-block">
                  {{ $t('generic.domain') }} <span class="text-red font-weight-bold">*</span>
                </label>
                <v-select
                  v-model="form.domain_id"
                  :items="domainOptions"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  color="primary"
                  prepend-inner-icon="mdi-domain"
                  density="comfortable"
                  disabled
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col cols="12">
                <label class="field-label mb-1 d-block">
                  {{ $t('promptmanagementtemplates.notes') }} <span class="optional-label">{{ $t('promptmanagementtemplates.optionalLabel') }}</span>
                </label>
                <v-textarea
                  v-model="form.notes"
                  variant="outlined"
                  color="primary"
                  density="comfortable"
                  rows="4"
                  maxlength="500"
                  :placeholder="$t('promptmanagementtemplates.enterNotes')"
                  hide-details
                >
                  <template #append-inner>
                    <span class="char-counter notes-counter">{{ form.notes.length }} / 500</span>
                  </template>
                </v-textarea>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!-- Template Steps section -->
      <v-row class="mt-6 px-2">
        <v-col cols="12">
          <v-card variant="outlined" rounded="lg" class="pa-6" style="border-color: #9E9E9E; background-color: white;">
            <div class="d-flex align-start justify-space-between">
              <div>
                <h2 class="text-h5 font-weight-bold mb-1" style="color: #000;">
                  {{ $t('promptmanagementtemplates.templateSteps') }} <span class="text-red font-weight-bold">*</span>
                </h2>
                <p class="text-body-2 text-medium-emphasis mt-1">
                  {{ $t('promptmanagementtemplates.templateStepsHint') }}
                </p>
              </div>
              <v-btn
                variant="outlined"
                color="#0D3B6E"
                prepend-icon="mdi-plus"
                style="background-color: white;"
                class="ml-4 mt-1 flex-shrink-0"
                @click="addStep"
              >
                {{ $t('promptmanagementtemplates.addStep') }}
              </v-btn>
            </div>

            <v-alert type="info" variant="tonal" density="compact" icon="mdi-paperclip" class="mt-4">
              <div class="text-body-2">{{ $t('promptmanagementtemplates.stimulusMaterialHelp') }}</div>
              <ul class="text-body-2 pl-5 mt-1">
                <li>{{ $t('promptmanagementtemplates.stimulusMaterialHelpBias') }}</li>
                <li>{{ $t('promptmanagementtemplates.stimulusMaterialHelpCost') }}</li>
              </ul>
            </v-alert>

            <v-divider class="my-4" />

            <div v-if="form.steps && form.steps.length > 0">
              <draggable
                v-model="form.steps"
                item-key="step_id"
                handle=".drag-handle"
                animation="200"
                @end="renumberSteps"
              >
                <template #item="{ element: step, index }">
                  <div class="step-item pa-4 mb-3 d-flex align-start ga-4">
                    <v-icon
                      class="drag-handle flex-shrink-0 align-self-center"
                      style="cursor: grab; color: #BDBDBD;"
                      size="20"
                    >
                      mdi-drag-vertical
                    </v-icon>

                    <div class="flex-grow-1" style="min-width: 0;">
                      <div class="d-flex align-start ga-4">
                        <div style="flex: 1;" class="d-flex align-center ga-2">
                          <div class="step-order flex-shrink-0">{{ step.order }}</div>
                          <div class="flex-grow-1">
                            <label class="field-label mb-1 d-block">
                              {{ $t('promptmanagementtemplates.stepName') }} <span class="text-red font-weight-bold">*</span>
                            </label>
                            <v-text-field
                              v-model="step.step_name"
                              variant="outlined"
                              density="comfortable"
                              :class="fieldErrors['step_' + index + '_name'] ? 'field-error' : ''"
                              :placeholder="$t('promptmanagementtemplates.enterStepName')"
                              maxlength="100"
                              :error="!!fieldErrors['step_' + index + '_name']"
                              hide-details
                            >
                              <template #append-inner>
                                <span class="char-counter">{{ (step.step_name || '').length }} / 100</span>
                              </template>
                            </v-text-field>
                          </div>
                        </div>

                        <div style="flex: 0 0 140px;">
                          <label class="field-label mb-1 d-block">Max Rounds</label>
                          <v-select
                            v-model="step.max_rounds"
                            :items="maxRoundsOptions"
                            item-title="label"
                            item-value="value"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                          />
                        </div>
                      </div>

                      <div class="mt-3">
                        <label class="field-label mb-1 d-block">
                          Facilitator Prompt <span class="text-red font-weight-bold">*</span>
                        </label>
                        <v-textarea
                          v-model="step.facilitator_prompt"
                          variant="outlined"
                          density="comfortable"
                          rows="3"
                          :class="fieldErrors['step_' + index + '_facilitator_prompt'] ? 'field-error' : ''"
                          placeholder="What should the facilitator do in this step?"
                          maxlength="10000"
                          :error="!!fieldErrors['step_' + index + '_facilitator_prompt']"
                          hide-details
                        >
                          <template #append-inner>
                            <span class="char-counter notes-counter">{{ (step.facilitator_prompt || '').length }} / 10000</span>
                          </template>
                        </v-textarea>
                      </div>

                      <div class="mt-3">
                        <label class="field-label mb-1 d-block">
                          Persona Prompt <span class="optional-label">(optional — leave blank to use the default)</span>
                        </label>
                        <v-textarea
                          v-model="step.persona_prompt"
                          variant="outlined"
                          density="comfortable"
                          rows="3"
                          placeholder="What should the personas do in this step?"
                          maxlength="10000"
                          hide-details
                        >
                          <template #append-inner>
                            <span class="char-counter notes-counter">{{ (step.persona_prompt || '').length }} / 10000</span>
                          </template>
                        </v-textarea>
                      </div>

                      <div class="mt-3" v-if="step.max_rounds === -1">
                        <label class="field-label mb-1 d-block">
                          Facilitator Completion Condition <span class="optional-label">(optional — leave blank to use the default)</span>
                        </label>
                        <v-textarea
                          v-model="step.facilitator_completion_condition"
                          variant="outlined"
                          density="comfortable"
                          rows="2"
                          placeholder="When should the facilitator consider this step done?"
                          maxlength="10000"
                          hide-details
                        >
                          <template #append-inner>
                            <span class="char-counter notes-counter">{{ (step.facilitator_completion_condition || '').length }} / 10000</span>
                          </template>
                        </v-textarea>
                      </div>

                      <v-switch
                        v-model="step.show_attachment"
                        :label="$t('promptmanagementtemplates.showAttachment')"
                        color="#0D3B6E"
                        density="compact"
                        inset
                        hide-details
                        class="mt-2"
                      />
                    </div>

                    <div class="d-flex flex-column flex-shrink-0">
                      <label class="field-label mb-1 d-block" style="visibility: hidden;">X</label>
                      <v-btn
                        variant="outlined"
                        color="error"
                        size="small"
                        style="min-width: 36px; width: 36px; height: 36px; padding: 0;"
                        @click="requestRemoveStep(index)"
                      >
                        <v-icon size="18">mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
              </draggable>
            </div>
            <div v-else>
              <div class="text-body-2 text-medium-emphasis text-center py-6">
                {{ $t('promptmanagementtemplates.noStepsEdit') }}
              </div>
              <div v-if="fieldErrors.no_steps" class="text-center pb-4">
                <span class="text-body-2 font-weight-medium" style="color: #D32F2F;">
                  {{ $t('promptmanagementtemplates.atLeastOneStepRequired') }}
                </span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Action buttons -->
      <v-row class="mt-6 px-2">
        <v-col cols="12">
          <div v-if="hasFieldErrors" class="text-center mb-2">
            <span class="text-body-1 font-weight-medium" style="color: #D32F2F;">
              {{ $t('promptmanagementtemplates.fillRequiredFieldsStar') }}
            </span>
          </div>
          <div class="d-flex justify-end ga-3">
            <v-btn
              variant="outlined"
              color="#0D3B6E"
              style="background-color: white;"
              @click="cancelForm"
            >
              {{ $t('generic.cancel') }}
            </v-btn>
            <v-btn
              prepend-icon="mdi-content-save"
              style="background-color: #0D3B6E; color: white;"
              :loading="saving"
              @click="saveTemplate"
            >
              {{ $t('promptmanagementtemplates.saveTemplate') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </template>

    <!-- Delete step confirmation dialog -->
    <v-dialog v-model="deleteStepDialog" max-width="420">
      <v-card>
        <v-card-title class="text-h6 pt-5 px-6">{{ $t('promptmanagementtemplates.deleteStepTitle') }}</v-card-title>
        <v-card-text class="px-6">
          {{ form.steps[stepToDelete]?.step_name
            ? $t('promptmanagementtemplates.deleteStepConfirmNamed', { order: stepToDelete !== null ? form.steps[stepToDelete]?.order : '', name: form.steps[stepToDelete]?.step_name })
            : $t('promptmanagementtemplates.deleteStepConfirm', { order: stepToDelete !== null ? form.steps[stepToDelete]?.order : '' }) }}
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn variant="text" @click="deleteStepDialog = false">{{ $t('generic.cancel') }}</v-btn>
          <v-btn color="error" variant="tonal" @click="confirmRemoveStep">{{ $t('generic.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel / leave page confirmation dialog -->
    <v-dialog v-model="cancelDialog" max-width="480">
      <v-card>
        <v-card-title class="text-h6 pt-5 px-6">{{ $t('promptmanagementtemplates.leavePageTitle') }}</v-card-title>
        <v-card-text class="px-6">
          {{ $t('promptmanagementtemplates.leavePageConfirm') }}
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn variant="text" @click="cancelDialog = false">{{ $t('promptmanagementtemplates.stay') }}</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="$router.push({ name: 'prompt-management-templates' })"
          >{{ $t('promptmanagementtemplates.leave') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success snackbar -->
    <v-snackbar v-model="saveSnackbar" color="success" location="bottom right" :timeout="2000">
      <v-icon class="mr-2">mdi-check-circle-outline</v-icon>
      {{ $t('promptmanagementtemplates.templateSavedSuccess') }}
    </v-snackbar>
  </v-container>
</template>

<script>
import draggable from 'vuedraggable';
import { get_meeting_template, create_meeting_template, update_meeting_template, get_current_user_from_storage, build_domain_options, get_default_domain } from '@/api';
import { editableTemplateStep, templateStepsPayload } from '@/utils/meetingTemplateSteps';

export default {
  name: 'PromptManagementTemplateForm',
  components: { draggable },

  props: {
    templateId: { type: String, default: null },
    mode: { type: String, default: 'new' },
  },

  data() {
    return {
      loading: false,
      saving: false,
      fieldErrors: {},
      saveSnackbar: false,
      deleteStepDialog: false,
      stepToDelete: null,
      cancelDialog: false,
      initialForm: null,
      currentUser: get_current_user_from_storage(),
      form: {
        template_name: '',
        domain_id: this.$route.query.domain || get_default_domain(),
        notes: '',
        steps: [],
        group_id: null,
      },
    };
  },

  computed: {
    isNew() { return this.mode === 'new'; },
    isView() { return this.mode === 'view'; },
    isEdit() { return this.mode === 'edit'; },
    isSuperAdmin() {
      return this.currentUser?.role_id === 'super_admin';
    },
    isDefaultTemplate() {
      return this.form.group_id === null || this.form.group_id === undefined;
    },
    canEditTemplate() {
      if (this.isDefaultTemplate) return this.isSuperAdmin;
      return true;
    },

    domainLabel() {
      const found = this.domainOptions.find(d => d.value === this.form.domain_id);
      return found ? found.title : this.form.domain_id;
    },

    domainOptions() {
      return build_domain_options(this.$t);
    },

    hasFieldErrors() {
      return Object.keys(this.fieldErrors).length > 0;
    },

    isFormDirty() {
      return JSON.stringify(this.form) !== this.initialForm;
    },

    maxRoundsOptions() {
      return [
        { label: 'Dynamic', value: -1 },
        ...Array.from({ length: 3 }, (_, i) => ({ label: String(i + 1), value: i + 1 })),
      ];
    },
  },

  async mounted() {
    if (this.templateId && (this.isView || this.isEdit)) {
      await this.loadTemplate();
    } else {
      this.initialForm = JSON.stringify(this.form);
    }
  },

  methods: {
    cancelForm() {
      if (this.isView) {
        this.$router.push({ name: 'prompt-management-templates' });
        return;
      }
      // New and Edit mode: go back to template list (with dirty check)
      if (this.isFormDirty) {
        this.cancelDialog = true;
      } else {
        this.$router.push({ name: 'prompt-management-templates' });
      }
    },

    requestRemoveStep(index) {
      this.stepToDelete = index;
      this.deleteStepDialog = true;
    },

    confirmRemoveStep() {
      if (this.stepToDelete !== null) {
        this.form.steps.splice(this.stepToDelete, 1);
        this.form.steps.forEach((s, i) => { s.order = i + 1; });
      }
      this.deleteStepDialog = false;
      this.stepToDelete = null;
    },

    renumberSteps() {
      this.form.steps.forEach((s, i) => { s.order = i + 1; });
    },

    newStepId() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      return `step_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    },

    addStep() {
      const lastOrder = this.form.steps.length > 0
        ? Math.max(...this.form.steps.map(s => s.order))
        : 0;
      this.form.steps.push({
        step_id: this.newStepId(),
        order: lastOrder + 1,
        step_name: '',
        facilitator_prompt: '',
        persona_prompt: '',
        max_rounds: -1,
        facilitator_completion_condition: '',
        show_attachment: true,
      });
    },

    async saveTemplate() {
      const errors = {};
      if (!(this.form.template_name || '').trim()) errors.template_name = true;
      if (this.form.steps.length === 0) errors.no_steps = true;
      this.form.steps.forEach((step, index) => {
        if (!(step.step_name || '').trim()) errors['step_' + index + '_name'] = true;
        if (!(step.facilitator_prompt || '').trim()) errors['step_' + index + '_facilitator_prompt'] = true;
      });
      this.fieldErrors = errors;
      if (Object.keys(errors).length > 0) return;

      let response;
      this.saving = true;
      try {
        const groupId = this.isSuperAdmin ? 'nttdata' : (this.currentUser?.group_ids?.[0] ?? null);
        const payload = {
          template_name: this.form.template_name.trim(),
          domain_id: this.form.domain_id,
          notes: this.form.notes.trim(),
          steps: templateStepsPayload(this.form.steps),
          ...(this.isNew ? { group_id: groupId } : {}),
        };
        if (this.isEdit && this.templateId) {
          response = await update_meeting_template(this.templateId, payload);
        } else {
          response = await create_meeting_template(payload);
        }
        this.saveSnackbar = true;
        const newId = this.isEdit ? this.templateId : (response?.data?.template_id || null);
        setTimeout(() => {
          if (newId) {
            this.$router.push({ name: 'prompt-management-template-view', params: { templateId: newId }, query: { domain: payload.domain_id } });
          } else {
            this.$router.push({ name: 'prompt-management-templates' });
          }
        }, 1800);
      } catch (error) {
        console.error('Failed to save template:', error.response?.data || error);
      } finally {
        this.saving = false;
      }
    },

    async loadTemplate() {
      this.loading = true;
      try {
        const response = await get_meeting_template(this.templateId);
        const t = response.data;
        this.form.template_name = t.template_name || '';
        this.form.domain_id = t.domain_id || this.$route.query.domain || get_default_domain();
        this.form.notes = t.notes || '';
        this.form.steps = (t.steps || []).map(editableTemplateStep);
        this.form.group_id = t.group_id ?? null;
        this.initialForm = JSON.stringify(this.form);
      } catch (error) {
        console.error('Failed to load template:', error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.field-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #424242;
}
.optional-label {
  font-size: 0.8rem;
  font-weight: 400;
  color: #9E9E9E;
}
.char-counter {
  font-size: 0.72rem;
  color: #9E9E9E;
  white-space: nowrap;
  align-self: flex-end;
  padding-bottom: 2px;
}
.notes-counter {
  align-self: flex-end;
}
.step-item {
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  background-color: #FAFAFA;
}
.step-order {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  min-width: 32px;
  min-height: 32px;
  color: #000000;
  font-weight: 700;
  font-size: 1.2rem;
  margin: 10px;
}
.field-error :deep(.v-field__outline) {
  --v-field-border-width: 2px;
  --v-field-border-opacity: 1;
}
.field-error :deep(.v-field--error .v-field__outline) {
  color: #D32F2F;
}
</style>
